<template>
  <div>
    <div v-if="status==0" class="container-fluid text-start">
      <h3 class="mt-3 mb-1">1. Select Practitioner</h3>
      <div class="row my-2">
        <div class="col-sm-1 pt-1 gdq-font-size-80">Target practitioner</div>
        <div class="col-sm-2 gdq-font-size-80">
          <div class="input-group input-group-sm">
            <input
              v-model="search"
              placeholder="Search..."
              type="text"
              @input="consultants_filter"
              autocomplete="off"
              class="form-control gdq-font-size-80"
            />
            <button
              class="btn btn-outline-secondary gdq-font-size-80"
              type="button"
              @click="search = ''"
            >
              X
            </button>
          </div>
          <div v-if="selectedconsultant" class="text-primary mt-1">
            {{ selectedconsultant.consultant_name }} ({{ selectedconsultant.email }})
            <fa-icon
              @click.prevent="clearConsultant"
              name="times"
              style="cursor: pointer;"
            />
          </div>
        </div>
        <div class="col-sm-9 gdq-font-size-80">
          <div class="my-1" style="position: relative; height: 170px; overflow: hidden;">
            <div style="height: 170px; overflow-y: auto;">
              <div v-if="results.length == 0" class="text-primary">
                No practitioners found
              </div>
              <table v-else class="table table-sm table-hover">
                <thead>
                  <tr>
                    <th>ID</th>
                    <th>Name</th>
                    <th>Email</th>
                  </tr>
                </thead>
                <tbody>
                  <tr
                    v-for="item in results"
                    :key="item.id"
                    style="cursor: pointer;"
                    @click="setConsultant(item)"
                  >
                    <td>{{ item.id }}</td>
                    <td>{{ item.consultant_name }}</td>
                    <td>{{ item.email }}</td>
                  </tr>
                </tbody>
              </table>
            </div>
            <div v-if="searching" class="text-center text-success" style="position: absolute; top: 0; left: 0; right: 0; z-index: 2;">
              <div class="spinner-border spinner-border-sm me-2" role="status">
                <span class="visually-hidden">Loading...</span>
              </div>
              <strong>Loading...</strong>
            </div>
          </div>
        </div>
      </div>
      <h3 v-if="selectedconsultant" class="mt-3 mb-1">2. Set test data parameters</h3>
      <div v-if="selectedconsultant" class="row my-2">
        <div class="col-sm-1 pt-1 gdq-font-size-80">Survey type</div>
        <div class="col-sm-2">
          <select 
            class="form-select gdq-font-size-80"
            :options="surveytypes" 
            v-model="surveytype_id"
            @change="surveytype_changed()"  
          >
            <option :value="0">Select survey type</option>
            <option v-for="type in surveytypes" :key="type.value" :value="type.value">
              {{ type.text }}
            </option>
          </select>
        </div>
        <div class="col-sm-1 pt-1 gdq-font-size-80">Normvalues</div>
        <div class="col-sm-2">
          <select class="form-select gdq-font-size-80" v-model="normvalues_id">
            <option :value="0">Select normvalues</option>
            <option v-for="norm in normvalues" :key="norm.value" :value="norm.value">
              {{ norm.text }}
            </option>
          </select>
        </div>
        <div class="col-sm-1 pt-1 gdq-font-size-80">Language</div>
        <div class="col-sm-2">
          <select class="form-select gdq-font-size-80" v-model="language_id">
            <option :value="0">Select language</option>
            <option v-for="lang in languages" :key="lang.value" :value="lang.value">
              {{ lang.text }}
            </option>
          </select>
        </div>
      </div>
      <div v-if="error != ''" class="alert alert-danger alert-dismissible fade show my-2">
        {{ error }}
        <button type="button" class="btn-close" @click="error = ''" aria-label="Close"></button>
      </div>
      <h3 v-if="parametersSelected" class="mt-3 mb-1">3. Create test data groups and surveys</h3>
      <div v-if="parametersSelected" class="row my-1">
        <div class="col-sm-3 gdq-font-size-80">
          Group Name
        </div>
        <div class="col-sm-1 gdq-font-size-80">
          Members
        </div>
        <div class="col-sm-1 gdq-font-size-80">
          Subgroups
        </div>
        <div class="col-sm-1 gdq-font-size-80">
          Surveys
        </div>
        <div class="col-sm-2 gdq-font-size-80">
          Start date
        </div>
        <div class="col-sm-2 gdq-font-size-80">
          Surveys interval months
        </div>
        <div class="col-sm-2">
          <button
            class="btn btn-primary gdq-font-size-80 col-8"
            @click.prevent="addGroup"
          >
            <fa-icon
                class="me-2"
                name="plus"
            />Add group to create
          </button>
        </div>      
      </div>
      <template v-if="parametersSelected">
      <div class="row py-2" v-bind:key="group.id" v-for="group in groups">
        <div class="col-sm-3">
          <input
            class="form-control gdq-font-size-80"
            v-model="group.groupName"
          />
        </div>
        <div class="col-sm-1">
          <input
            class="form-control gdq-font-size-80"
            v-model="group.numberMembers"
            @input="numberMembers_input(group)"
          />
        </div>
        <div class="col-sm-1">
          <input
            class="form-check-input mt-2"
            type="checkbox"
            v-model="group.subgroups"
            :disabled="surveytype_id != 1 || Number(group.numberMembers) < 4"
          />
        </div>
        <div class="col-sm-1">
          <input
            class="form-control gdq-font-size-80"
            v-model="group.numberSurveys"
          />
        </div>
        <div class="col-sm-2">
          <datetime
            format="yyyy-LL-dd"
            v-model="group.startDate"
            value-zone="Europe/Stockholm"
            zone="Europe/Stockholm"
            :phrases="{ok: 'OK', cancel: 'Cancel'}"
          >
          </datetime>
        </div>
        <div class="col-sm-2">
          <input
            class="form-control gdq-font-size-80"
            v-model="group.monthsBetween"
          />
        </div>
        <div class="col-sm-2">
          <button
            class="btn btn-danger gdq-font-size-80 col-8"
            @click.prevent="removeGroup(group.id)"
          >
            <fa-icon
                class="me-2"
                name="trash"
            />Remove group
          </button>
        </div>
      </div>
      </template>
      <div v-if="parametersSelected" class="row my-2">
        <div class="col-sm-3">
          <button @click.prevent="createSurveys" class="btn btn-primary gdq-font-size-80" :disabled="groups.length===0">Create groups and surveys</button>
        </div>
      </div>
    </div>
    <div v-if="status==1" class="container-fluid text-start">
      <div class="gdq-font-size-90 fw-bold mt-3 mb-1">Generating testdata...</div>
    </div>
    <div v-if="status==2" class="container-fluid text-start">
      <div class="gdq-font-size-90 fw-bold mt-3 mb-1">Testdata generated</div>
    </div>
  </div>
</template>
<script>
import _ from 'lodash';
import Datetime from './CustomControls/Datetime.vue';
import axios from 'axios'
import { config } from '../../config';
const base_url = config.base_url;

export default {
  name: 'GenerateTestData',
  components: {
    Datetime
  },
  created() {
    this.surveytypes_get()
    this.normvalues_get()
    this.languages_get()
    this.consultants_searchget()
  },
  data: function() {
    return {
      status: 0,
      surveytype_id: 0,
      surveytypes: [],
      normvalues_id: 0,
      normvalues: [],
      language_id: 0,
      languages: [],
      groups: [],
      search: '',
      searching: false,
      results: [],
      selectedconsultant: null,
      error: '',
    }
  },
  computed: {
    parametersSelected() {
      return this.surveytype_id != 0 && this.normvalues_id != 0 && this.language_id != 0;
    },
  },
  watch: {
    search: function() {
      this.searching = true
      this.consultants_filter()
    },
  },
  methods: {
    createSurveys() {
      this.status = 1;
      this.error = '';
      const record = {
        groups: this.groups,
        surveytype_id: this.surveytype_id,
        normvalues_id: this.normvalues_id,
        language_id: this.language_id,
        user_id: this.selectedconsultant ? this.selectedconsultant.id : 0,
      }
      axios.defaults.headers.common['Authorization'] = 'Bearer:'+this.$store.state.token;
      return new Promise((resolve) => {
        axios.post(
          base_url+"/gdq_testdata_create",
          JSON.stringify(record), {
            headers: {
                "Accept": "application/json",
                "Content-type": "application/json",
            }
          }
        )
        .then((resp) => {
          this.status = 2;
          resolve(resp);
        })
        .catch((err) => {
          this.status = 0;
          if (err.response && err.response.data && err.response.data.error) {
            this.error = err.response.data.error;
          } else {
            this.error = 'Error creating test data.';
          }
        })
      });
    },
    consultants_filter: _.debounce(function() {
      setTimeout(function() {
        this.consultants_searchget();
        this.searching = false;
      }.bind(this),500)
    },300),
    consultants_searchget() {
      var filter = {};
      if (this.search != "") {
        filter.family_name = {
          operator: "LIKE",
          value: "%" + this.search + "%",
        };
        filter.first_name = {
          operator: "LIKE",
          value: "%" + this.search + "%",
        };
        filter.email = {
          operator: "LIKE",
          value: "%" + this.search + "%",
        };
      }
      this.$store
        .dispatch("consultants_fetch", {
          start: 1,
          limit: 20,
          orderby: "id",
          order: "ASC",
          filter: filter,
          label_id: 0,
          usergroup_id: 0,
          consultantstatus_id: 0,
          consultantgroup_id: 0,
          deleted: 0,
          enterprise_id: 0,
        })
        .then((resp) => {
          const result = Array.isArray(resp.data?.result) ? resp.data.result : [];
          this.results = JSON.parse(JSON.stringify(result)).map((r) => ({
            id: r.id,
            consultant_name: r.consultant_name,
            email: r.email,
          }));
          this.searching = false;
        });
    },
    setConsultant(item) {
      this.selectedconsultant = {
        id: item.id,
        consultant_name: item.consultant_name,
        email: item.email,
      };
      this.search = '';
    },
    clearConsultant() {
      this.selectedconsultant = null;
    },
    addGroup() {
      const id = this.groups.length + 1;
      this.groups.push({
        id: id,
        groupName: `Group ${id}`,
        numberMembers: 3,
        subgroups: false,
        numberSurveys: 3,
        startDate: '2021-01-01',
        monthsBetween: 3,
      });

    },
    removeGroup(id) {
      this.groups = this.groups.filter(g => g.id != id);
    },
    surveytype_changed() {
      if (this.surveytype_id != 1) {
        this.groups.forEach(g => { g.subgroups = false; });
      }
      this.languages_get();
      this.normvalues_get();
    },
    numberMembers_input(group) {
      if (Number(group.numberMembers) < 4) {
        group.subgroups = false;
      }
    },
    surveytypes_get() {
      axios.defaults.headers.common['Authorization'] = 'Bearer:'+this.$store.state.token;
      return new Promise((resolve) => {
        axios.get(base_url+"/g_surveytypes")
        .then((resp) => {
          this.surveytypes = resp.data.result.map(
            r => { return { value: r.id, text: r.surveytype_name } }
          );
          resolve(resp);
        });                
      });
    },
    normvalues_get() {
      axios.defaults.headers.common['Authorization'] = 'Bearer:'+this.$store.state.token;
      return new Promise((resolve) => {
        axios.get(base_url+"/gdq_normvalues_countries")
        .then((resp) => {
          this.normvalues = resp.data.map(
            r => { return { value: r.country_id, text: r.country_name } }
          );
          resolve(resp);
        });                
      });

    },
    languages_get() {
      axios.defaults.headers.common['Authorization'] = 'Bearer:'+this.$store.state.token;
      let filter = {};
      if (this.surveytype_id == 2) { filter.language_enabled_gdq_short =  { operator: "=", value: 1, }}
      if (this.surveytype_id == 3) { filter.language_enabled_gdq_osc =  { operator: "=", value: 1, }}
      return new Promise((resolve) => {
        axios.get(base_url+"/g_languages?filter="+JSON.stringify(filter))
        .then((resp) => {
          this.languages = resp.data.result.map(
            r => { return { value: r.id, text: r.language_name } }
          );
          resolve(resp);
        });                
      });
    },
  },
}
</script>
<style scoped>

</style>
